import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Grid, Modal, CircularProgress } from "@mui/material";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import { PlansApiService } from "api/plans.api";
import { modalContainerStyle } from "pages/components/common/Styles";

/**
 * PricingModal — shows the 9 canonical F2 plan SKUs (Core Connect, Operations,
 * Compliance, AFMS Portal Renewal, Full Rooftop, and the four Enterprise tiers)
 * seeded by flex2-api PlanCatalogSeedController. Renders two tabs:
 *   - "Rooftop plans" — per-site base SKUs
 *   - "Enterprise"    — per-dealership-group tiers
 *
 * Current plan is highlighted. Clicking "Select" posts a change request.
 */
const PricingModal = ({ open = false, onClose, organizationId = undefined, onChanged = undefined }) => {
  const [loading, setLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [currentPlanUuid, setCurrentPlanUuid] = useState(null);
  const [tab, setTab] = useState("site");
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const [catalogResp, currentResp] = await Promise.all([
          PlansApiService.list(),
          PlansApiService.current(organizationId),
        ]);
        if (cancelled) return;
        setPlans(catalogResp.data?.data || catalogResp.data || []);
        setCurrentPlanUuid(
          currentResp.data?.data?.subscriptionPlan?.uuid ||
            currentResp.data?.data?.uuid ||
            null,
        );
      } catch (err) {
        toast.error("Failed to load pricing catalog");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [open, organizationId]);

  const { sitePlans, enterprisePlans } = useMemo(() => {
    const site = [];
    const ent = [];
    for (const p of plans) {
      const planType = p.planType ?? p.PlanType;
      if (planType === 2) ent.push(p);
      else site.push(p);
    }
    const byPrice = (a, b) => (a.price ?? a.Price ?? 0) - (b.price ?? b.Price ?? 0);
    site.sort(byPrice);
    ent.sort(byPrice);
    return { sitePlans: site, enterprisePlans: ent };
  }, [plans]);

  const handleSelect = async (plan) => {
    const uuid = plan.uuid || plan.Uuid;
    if (!uuid) return;
    if (uuid === currentPlanUuid) {
      toast("Already on this plan");
      return;
    }
    try {
      setSaving(uuid);
      await PlansApiService.changePlan(uuid);
      toast.success(`Plan change requested: ${plan.name || plan.Name}`);
      setCurrentPlanUuid(uuid);
      if (typeof onChanged === "function") onChanged(plan);
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Plan change failed");
    } finally {
      setSaving(null);
    }
  };

  const currentPlans = tab === "enterprise" ? enterprisePlans : sitePlans;

  return (
    <Modal open={!!open} onClose={onClose} aria-labelledby="pricing-modal-title">
      <SoftBox sx={{ ...modalContainerStyle, width: { xs: "92vw", md: 960 }, maxHeight: "90vh", overflow: "auto" }}>
        <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <SoftTypography id="pricing-modal-title" variant="h4" fontWeight="bold">
            Plans & Pricing
          </SoftTypography>
          <SoftButton variant="text" color="dark" onClick={onClose}>
            Close
          </SoftButton>
        </SoftBox>

        <SoftBox display="flex" gap={1} mb={3}>
          <SoftButton
            size="small"
            variant={tab === "site" ? "gradient" : "outlined"}
            color={tab === "site" ? "primary" : "dark"}
            onClick={() => setTab("site")}
          >
            Rooftop plans
          </SoftButton>
          <SoftButton
            size="small"
            variant={tab === "enterprise" ? "gradient" : "outlined"}
            color={tab === "enterprise" ? "primary" : "dark"}
            onClick={() => setTab("enterprise")}
          >
            Enterprise
          </SoftButton>
        </SoftBox>

        {loading ? (
          <SoftBox display="flex" justifyContent="center" py={6}>
            <CircularProgress size={32} />
          </SoftBox>
        ) : (
          <Grid container spacing={2}>
            {currentPlans.map((plan) => {
              const uuid = plan.uuid || plan.Uuid;
              const name = plan.name || plan.Name;
              const description = plan.description || plan.Description;
              const price = plan.price ?? plan.Price;
              const annual = plan.annualPrice ?? plan.AnnualPrice;
              const features = plan.features || plan.Features || [];
              const isCurrent = uuid === currentPlanUuid;

              return (
                <Grid item xs={12} md={6} lg={4} key={uuid}>
                  <SoftBox
                    sx={{
                      border: (theme) =>
                        `1px solid ${isCurrent ? theme.palette.primary.main : theme.palette.grey[300]}`,
                      borderRadius: 2,
                      p: 2.5,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      bgcolor: isCurrent ? "#eef2ff" : "white",
                    }}
                  >
                    <SoftTypography variant="h5" fontWeight="bold">
                      {name}
                    </SoftTypography>
                    {description ? (
                      <SoftTypography variant="body2" color="text" sx={{ mt: 0.5, mb: 1.5 }}>
                        {description}
                      </SoftTypography>
                    ) : null}

                    <SoftBox display="flex" alignItems="baseline" gap={1} mb={1}>
                      <SoftTypography variant="h3" fontWeight="bold">
                        ${price != null ? Number(price).toFixed(0) : "—"}
                      </SoftTypography>
                      <SoftTypography variant="caption" color="text">
                        /mo
                      </SoftTypography>
                    </SoftBox>
                    {annual != null ? (
                      <SoftTypography variant="caption" color="text" sx={{ mb: 1.5 }}>
                        ${Number(annual).toFixed(0)}/yr (10% off)
                      </SoftTypography>
                    ) : null}

                    <SoftBox component="ul" sx={{ pl: 3, m: 0, mb: 2, flex: 1 }}>
                      {(features || []).slice(0, 8).map((f, i) => (
                        <SoftTypography
                          key={i}
                          component="li"
                          variant="body2"
                          color="text"
                          sx={{ mb: 0.5 }}
                        >
                          {f.displayName || f.DisplayName || f.featureKey || f.FeatureKey}
                        </SoftTypography>
                      ))}
                    </SoftBox>

                    <SoftButton
                      fullWidth
                      variant="gradient"
                      color={isCurrent ? "dark" : "primary"}
                      disabled={isCurrent || saving === uuid}
                      onClick={() => handleSelect(plan)}
                    >
                      {isCurrent
                        ? "Current plan"
                        : saving === uuid
                        ? "Saving…"
                        : "Select"}
                    </SoftButton>
                  </SoftBox>
                </Grid>
              );
            })}
            {currentPlans.length === 0 ? (
              <Grid item xs={12}>
                <SoftBox textAlign="center" py={6}>
                  <SoftTypography variant="body2" color="text">
                    No plans to show. Ask an admin to run the plan catalog seed.
                  </SoftTypography>
                </SoftBox>
              </Grid>
            ) : null}
          </Grid>
        )}
      </SoftBox>
    </Modal>
  );
};

PricingModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  organizationId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onChanged: PropTypes.func,
};

export default PricingModal;
